
import { defineComponent } from "vue";
import * as THREE from "three";
import { scrollDisabled } from '@/store/uiState';
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const loader = new GLTFLoader();

export async function loadModel(
  path: string
): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    loader.load(
      path,
      (gltf) => {
        const model = gltf.scene;
        // Масштабирование если нужно
        // model.scale.set(0.01, 0.01, 0.01);
        resolve(model);
      },
      undefined,
      (error) => {
        console.error("Ошибка загрузки модели:", error);
        reject(error);
      }
    );
  });
}

export default defineComponent({
  props: {
    structure: {type: String},
    rollsList: {type: Object},
    palletsList: {type: Object},
    container: {type: Object},
  },
  data() {
    return {
      CARGOS: [],
      cameraRotation: { x: 0, y: 0 },
      animationId: null,
      previousMousePosition: { x: 0, y: 0 },
      isRotatingCamera: false,
      selectedCargo: null,
      cargos3DCount: 0,
      SNAP_DIST: 25,
    };
  },

  created () {
    this.scene = null,
    this.camera = null,
    this.renderer = null,
    this.mouse = new THREE.Vector2(),
    this.container3d = null,
    this.raycaster = new THREE.Raycaster(),
    this.cargos3d = [],
    this.dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0),
    this.dragPoint = new THREE.Vector3()
    this.ground = -1000
    if (this.container.type === 0) {
      this.ground = 0
    }
  },

  methods: {
    animate () {
      this.animationId = requestAnimationFrame(() => this.animate())
      const helper = new THREE.AxesHelper(1000)
      this.scene.add(helper)
      this.renderer.render(this.scene, this.camera)
    },
  
    parseStructure(structureString) {
      const parts = structureString.split("|");
      const version = parts[0]; // первая часть - версия

      const rolls = [];
      const pallets = [];

      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];

        if (part.startsWith("r_")) {
          // Обработка рулонов
          const data = part.substring(2).split("_");
          const roll = {
            type: "roll",
            x: parseFloat(data[0]),
            y: parseFloat(data[1]),
            z: parseFloat(data[2]),
            indexes: data.slice(3).map(Number),
          };
          rolls.push(roll);
        } else if (part.startsWith("p_")) {
          // Обработка паллет
          const data = part.substring(2).split("_");
          const palletStack: any = {
            type: "pallet",
            x: parseFloat(data[0]),
            y: parseFloat(data[1]),
            z: parseFloat(data[2]),
          };

          // Обрабатываем пары индекс-поворот
          const pairs = [];
          for (let j = 3; j < data.length; j += 2) {
            if (j + 1 < data.length) {
              pairs.push({
                index: parseInt(data[j]),
                rotated: data[j + 1].toLowerCase() === "true",
              });
            }
          }

          palletStack.pallets = pairs;
          pallets.push(palletStack);
        }
      }

      return {
        version,
        rolls,
        pallets,
      };
    },
    
    buildCargos() {
      const parsed = this.parseStructure(this.structure);

      const cargos = [];
      parsed.rolls.forEach((column) => {
        let rollsHeight = 0;
        for (let i = 0; i < column.indexes.length; i++) {
          const roll = this.rollsList.find(r => r.id == column.indexes[i]);
          cargos.push({
            type: "cylinder",
            name: roll.name,
            diameter: roll.diameter,
            height: roll.height,
            x: column.x,
            y: rollsHeight + roll.height * 0.5 + column.z,
            z: column.y,
            color: roll.color,
          });
          rollsHeight += roll.height;
        }
      });

      parsed.pallets.forEach((column) => {
        let palletsHeight = 0;
        for (let i = 0; i < column.pallets.length; i++) {
          const pallet = this.palletsList.find(p => p.id == column.pallets[i].index);
          let l = pallet.length;
          let w = pallet.width;

          if (column.pallets[i].rotated) {
            const t = l;
            l = w;
            w = t;
          }

          cargos.push({
            type: "box",
            name: pallet.name,
            width: l,
            height: pallet.height,
            depth: w,
            x: column.x,
            y: palletsHeight + pallet.height * 0.5 + column.z,
            z: column.y,
            color: pallet.color,
          });
          palletsHeight += pallet.height;
        }
      });

      this.CARGOS = cargos;
    },

    createDoors() {
      if (this.container.type == 1) {
        const boxDoorWidth = Math.min(3800, this.container.length);

        const doorWidth = boxDoorWidth / 2; // Половина ширины контейнера
        const doorHeight = this.container.height;
        const doorThickness = 20; // Толщина двери
        
        const doorPositionZ = this.container.width; // В конце контейнера
        const doorPositionY = doorHeight / 2; // По центру высоты
        
        // Левая дверь
        const leftDoorGeo = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const doorMat = new THREE.MeshPhongMaterial({ 
          color: 0xcccccc, 
          shininess: 100, 
          emissive: 0x000000,
          transparent: true,
          opacity: 0 // Полностью прозрачная
        });
        const leftDoor = new THREE.Mesh(leftDoorGeo, doorMat);
        
        // Позиционируем левую дверь
        leftDoor.position.set(
          this.container.length / 2 - doorWidth, // Смещение от края
          doorPositionY,
          doorPositionZ + doorThickness / 2
        );
        
        // Поворачиваем дверь относительно оси крепления (левого края)
        leftDoor.geometry.translate(doorWidth / 2, 0, 0); // Смещаем геометрию для поворота
        leftDoor.rotation.y = Math.PI + Math.PI / 3; // 60 градусов (π/3 радиан)
        
        // Добавляем контур
        const leftDoorEdges = new THREE.EdgesGeometry(leftDoorGeo);
        const leftDoorOutline = new THREE.LineSegments(
          leftDoorEdges,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        leftDoorOutline.position.copy(leftDoor.position);
        leftDoorOutline.rotation.copy(leftDoor.rotation);
        //leftDoorOutline.geometry.translate(-doorWidth / 2, 0, 0);
        
        leftDoor.castShadow = true;
        leftDoor.receiveShadow = true;
        
        this.scene.add(leftDoor);
        this.scene.add(leftDoorOutline);

        
        const leftDoorGeo2 = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const doorMat2 = new THREE.MeshPhongMaterial({ 
          color: 0xcccccc, 
          shininess: 100, 
          emissive: 0x000000,
          transparent: true,
          opacity: 0 // Полностью прозрачная
        });
        const leftDoor2 = new THREE.Mesh(leftDoorGeo2, doorMat2);
        
        // Позиционируем левую дверь
        leftDoor2.position.set(
          this.container.length / 2 - doorWidth, // Смещение от края
          doorPositionY,
          0
        );
        
        // Поворачиваем дверь относительно оси крепления (левого края)
        leftDoor2.geometry.translate(doorWidth / 2, 0, 0); // Смещаем геометрию для поворота
        leftDoor2.rotation.y = - (Math.PI + Math.PI / 3); // 60 градусов (π/3 радиан)
        
        // Добавляем контур
        const leftDoorEdges2 = new THREE.EdgesGeometry(leftDoorGeo2);
        const leftDoorOutline2 = new THREE.LineSegments(
          leftDoorEdges2,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        leftDoorOutline2.position.copy(leftDoor2.position);
        leftDoorOutline2.rotation.copy(leftDoor2.rotation);
        //leftDoorOutline.geometry.translate(-doorWidth / 2, 0, 0);
        
        leftDoor2.castShadow = true;
        leftDoor2.receiveShadow = true;
        
        this.scene.add(leftDoor2);
        this.scene.add(leftDoorOutline2);
        
        // Правая дверь
        const rightDoorGeo = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const rightDoor = new THREE.Mesh(rightDoorGeo, doorMat);
        
        // Позиционируем правую дверь
        rightDoor.position.set(
          this.container.length / 2 + doorWidth,
          doorPositionY,
          doorPositionZ + doorThickness / 2
        );
        
        // Поворачиваем дверь (зеркально)
        rightDoor.geometry.translate(doorWidth / 2, 0, 0);
        rightDoor.rotation.y = -Math.PI / 3; // -60 градусов
        
        // Добавляем контур
        const rightDoorEdges = new THREE.EdgesGeometry(rightDoorGeo);
        const rightDoorOutline = new THREE.LineSegments(
          rightDoorEdges,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        rightDoorOutline.position.copy(rightDoor.position);
        rightDoorOutline.rotation.copy(rightDoor.rotation);
        //rightDoorOutline.geometry.translate(doorWidth / 2, 0, 0);
        
        rightDoor.castShadow = true;
        rightDoor.receiveShadow = true;
        
        this.scene.add(rightDoor);
        this.scene.add(rightDoorOutline);

        const rightDoorGeo2 = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const rightDoor2 = new THREE.Mesh(rightDoorGeo2, doorMat);
        
        // Позиционируем правую дверь
        rightDoor2.position.set(
          this.container.length / 2 + doorWidth,
          doorPositionY,
          0
        );
        
        // Поворачиваем дверь (зеркально)
        rightDoor2.geometry.translate(doorWidth / 2, 0, 0);
        rightDoor2.rotation.y = Math.PI / 3; // -60 градусов
        
        // Добавляем контур
        const rightDoorEdges2 = new THREE.EdgesGeometry(rightDoorGeo2);
        const rightDoorOutline2 = new THREE.LineSegments(
          rightDoorEdges2,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        rightDoorOutline2.position.copy(rightDoor2.position);
        rightDoorOutline2.rotation.copy(rightDoor2.rotation);
        //rightDoorOutline.geometry.translate(doorWidth / 2, 0, 0);
        
        rightDoor2.castShadow = true;
        rightDoor2.receiveShadow = true;
        
        this.scene.add(rightDoor2);
        this.scene.add(rightDoorOutline2);
      } else {
        const doorWidth = this.container.width / 2; // Половина ширины контейнера
        const doorHeight = this.container.height;
        const doorThickness = 20; // Толщина двери
        
        const doorPositionZ = this.container.width; // В конце контейнера
        const doorPositionY = doorHeight / 2; // По центру высоты
        
        // Левая дверь
        const leftDoorGeo = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const doorMat = new THREE.MeshPhongMaterial({ 
          color: 0xcccccc, 
          shininess: 100, 
          emissive: 0x000000,
          transparent: true,
          opacity: 0 // Полностью прозрачная
        });
        const leftDoor = new THREE.Mesh(leftDoorGeo, doorMat);
        
        // Позиционируем левую дверь
        leftDoor.position.set(
          this.container.length, // Смещение от края
          doorPositionY,
          doorPositionZ + doorThickness / 2
        );
        
        // Поворачиваем дверь относительно оси крепления (левого края)
        leftDoor.geometry.translate(doorWidth / 2, 0, 0); // Смещаем геометрию для поворота
        leftDoor.rotation.y = -Math.PI / 9; // 60 градусов (π/3 радиан)
        
        // Добавляем контур
        const leftDoorEdges = new THREE.EdgesGeometry(leftDoorGeo);
        const leftDoorOutline = new THREE.LineSegments(
          leftDoorEdges,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        leftDoorOutline.position.copy(leftDoor.position);
        leftDoorOutline.rotation.copy(leftDoor.rotation);
        //leftDoorOutline.geometry.translate(-doorWidth / 2, 0, 0);
        
        leftDoor.castShadow = true;
        leftDoor.receiveShadow = true;
        
        this.scene.add(leftDoor);
        this.scene.add(leftDoorOutline);
        
        // Правая дверь
        const rightDoorGeo = new THREE.BoxGeometry(doorWidth, doorHeight, doorThickness);
        const rightDoor = new THREE.Mesh(rightDoorGeo, doorMat);
        
        // Позиционируем правую дверь
        rightDoor.position.set(
          this.container.length,
          doorPositionY,
          0
        );
        
        // Поворачиваем дверь (зеркально)
        rightDoor.geometry.translate(doorWidth / 2, 0, 0);
        rightDoor.rotation.y = Math.PI / 9; // -60 градусов
        
        // Добавляем контур
        const rightDoorEdges = new THREE.EdgesGeometry(rightDoorGeo);
        const rightDoorOutline = new THREE.LineSegments(
          rightDoorEdges,
          new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 2 })
        );
        rightDoorOutline.position.copy(rightDoor.position);
        rightDoorOutline.rotation.copy(rightDoor.rotation);
        //rightDoorOutline.geometry.translate(doorWidth / 2, 0, 0);
        
        rightDoor.castShadow = true;
        rightDoor.receiveShadow = true;
        
        this.scene.add(rightDoor);
        this.scene.add(rightDoorOutline);
      }
    },

    // ===== СОЗДАНИЕ КОНТЕЙНЕРА =====
    createContainer() {
      const containerGeo = new THREE.BoxGeometry(this.container.length, this.container.height, this.container.width);
      const containerMat = new THREE.MeshPhongMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        wireframe: false,
        transparent: true,
        opacity: 0.1,
      });
      this.container3d = new THREE.Mesh(containerGeo, containerMat);
      this.container3d.position.set(this.container.length / 2, this.container.height / 2, this.container.width / 2);
      this.container3d.castShadow = true;
      this.container3d.receiveShadow = true;
      this.scene.add(this.container3d);

      // Рёбра контейнера
      const edges = new THREE.EdgesGeometry(containerGeo);
      const wireframe = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x808285, linewidth: 1 }));
      wireframe.position.copy(this.container3d.position);
      this.scene.add(wireframe);

      // Полы и стены (для теней)
      const floorGeo = new THREE.PlaneGeometry(this.container.length, this.container.width);
      const floorMat = new THREE.MeshPhongMaterial({ color: 0xFFFFFF, side: THREE.DoubleSide });
      const floor = new THREE.Mesh(floorGeo, floorMat);
      floor.rotation.x = Math.PI / 2;
      floor.position.set(this.container.length / 2, -1, this.container.width / 2);
      floor.receiveShadow = true;
      this.scene.add(floor);

      // 🆕 Сетка (Grid)\
      this.maxSize = Math.max(this.container.length * 2, this.container.width * 2)
      const gridSize = this.maxSize;
      const gridDivisions = gridSize / 1000; // ячейка 1000x1000
      const gridHelper = new THREE.GridHelper(gridSize, gridDivisions, 0xcccccc, 0xeeeeee);
      gridHelper.position.set(this.container.length / 2, this.ground - 2, this.container.width / 2); // чуть выше пола
      this.scene.add(gridHelper);
    },

    // ===== СОЗДАНИЕ ГРУЗОВ =====
    createCargos() {
      this.CARGOS.forEach((cargoData, index) => {
        let mesh, outlineMesh;
        if (cargoData.type === "box") {
          const geo = new THREE.BoxGeometry(cargoData.width, cargoData.height, cargoData.depth);
          const mat = new THREE.MeshPhongMaterial({ color: cargoData.color, shininess: 100, emissive: 0x000000 });
          mesh = new THREE.Mesh(geo, mat);
          // Создаем геометрию для контура
          const edges = new THREE.EdgesGeometry(geo);
          const outlineMat = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
          outlineMesh = new THREE.LineSegments(edges, outlineMat);
        }
        else if (cargoData.type === "cylinder") {
          const geo = new THREE.CylinderGeometry(cargoData.diameter / 2, cargoData.diameter / 2, cargoData.height, 32);
          const mat = new THREE.MeshPhongMaterial({ color: cargoData.color, shininess: 80, emissive: 0x000000 });
          mesh = new THREE.Mesh(geo, mat);
          const edges = new THREE.EdgesGeometry(geo);
          const outlineMat = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
          outlineMesh = new THREE.LineSegments(edges, outlineMat);
        }

        mesh.position.set(cargoData.x, cargoData.y, cargoData.z);
        outlineMesh.position.copy(mesh.position);
        outlineMesh.rotation.copy(mesh.rotation);
        outlineMesh.scale.copy(mesh.scale);

        mesh.castShadow = true;
        mesh.receiveShadow = true;
        outlineMesh.visible = true; // Изначально контур невидим

        mesh.userData = {
          cargoIndex: index,
          ...cargoData,
          originalPosition: mesh.position.clone(),
          originalRotation: mesh.rotation.clone(),
          outline: outlineMesh, // Сохраняем ссылку на контурный меш
          dragPosition: mesh.position.clone(),
        };

        this.scene.add(mesh);
        this.scene.add(outlineMesh);
        this.cargos3d.push(mesh);
      });
      this.cargos3DCount = this.cargos3d.length;
    },

    // ===== ФУНКЦИЯ ОБНОВЛЕНИЯ ПОЗИЦИИ КАМЕРЫ =====
    updateCameraPosition() {
      // всегда ориентируемся на центр контейнера и вычисленный distance
      const centerX = this.container.length / 2;
      const centerY = this.container.height / 2;
      const centerZ = this.container.width / 2;
      const distance = this.camera.userData.distance || Math.max(this.container.length, this.container.width, this.container.height) * 2.8;

      this.camera.position.x = centerX + distance * Math.sin(this.cameraRotation.y) * Math.cos(this.cameraRotation.x);
      this.camera.position.y = centerY + distance * Math.sin(this.cameraRotation.x);
      this.camera.position.z = centerZ + distance * Math.cos(this.cameraRotation.y) * Math.cos(this.cameraRotation.x);

      this.camera.lookAt(centerX, centerY, centerZ);
    },

    // Функция проверки, находится ли груз внутри контейнера
    isInsideContainer2(x, y, cargoData) {
      if (cargoData.type == "cylinder") {
        if (x < -cargoData.diameter * 0.5 || x > this.container.length + cargoData.diameter * 0.5) {
          return false;
        }
        if (y < -cargoData.diameter * 0.5 || y > this.container.width + cargoData.diameter * 0.5) {
          return false;
        }
        return true;
      } else {
        if (x < -cargoData.width * 0.5 || x > this.container.length + cargoData.width * 0.5) {
          return false;
        }
        if (y < -cargoData.depth * 0.5 || y > this.container.width + cargoData.depth * 0.5) {
          return false;
        }
        return true;
      }
    },

    trySnapToNeighbors(cargo, targetPosition) {
      const cargoData = cargo.userData;

      for (let other of this.cargos3d) {
        const otherData = other.userData;

        if (cargoData.cargoIndex == otherData.cargoIndex) continue;

        if (cargoData.type == "box" && otherData.type == "box") {
          let points = [];

          points.push({ x: other.position.x - otherData.width * 0.5 - cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z });
          points.push({ x: other.position.x + otherData.width * 0.5 + cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z });

          points.push({ x: other.position.x - otherData.width * 0.5 - cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z + otherData.depth * 0.5 - cargoData.depth * 0.5 });
          points.push({ x: other.position.x + otherData.width * 0.5 + cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z - otherData.depth * 0.5 + cargoData.depth * 0.5 });
          
          points.push({ x: other.position.x + otherData.width * 0.5 + cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z + otherData.depth * 0.5 - cargoData.depth * 0.5 });
          points.push({ x: other.position.x - otherData.width * 0.5 - cargoData.width * 0.5, y: other.position.y - otherData.height * 0.5, z: other.position.z - otherData.depth * 0.5 + cargoData.depth * 0.5 });

          points.push({ x: other.position.x, y: other.position.y - otherData.height * 0.5, z: other.position.z - otherData.depth * 0.5 - cargoData.depth * 0.5 });
          points.push({ x: other.position.x, y: other.position.y - otherData.height * 0.5, z: other.position.z + otherData.depth * 0.5 + cargoData.depth * 0.5 });
          
          points.push({ x: other.position.x, y: other.position.y + otherData.height * 0.5, z: other.position.z });

          for (let point of points) {
            const dx = targetPosition.x - point.x;
            const dy = (targetPosition.y - cargoData.height * 0.5) - point.y;
            const dz = targetPosition.z - point.z;
            
            const distance = Math.sqrt(dx*dx + dz*dz);

            if (distance < 200) {
              return new THREE.Vector3(point.x, point.y + cargoData.height * 0.5, point.z);
            }
          }
        }
      }

      for (let other of this.cargos3d) {
        const otherData = other.userData;

        if (cargoData.cargoIndex == otherData.cargoIndex) continue;

        if (cargoData.type == "cylinder" && otherData.type == "cylinder") {
          const dx = targetPosition.x - other.position.x;
          const dy = (targetPosition.y - cargoData.height * 0.5) - (other.position.y + otherData.height * 0.5);
          const dz = targetPosition.z - other.position.z;
          
          const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);

          if (distance < 200) {
            return new THREE.Vector3(other.position.x, other.position.y + otherData.height * 0.5 + cargoData.height * 0.5, other.position.z);
          }
        }
      }

      for (let other of this.cargos3d) {
        const otherData = other.userData;

        if (cargoData.cargoIndex == otherData.cargoIndex) continue;

        if (cargoData.type == "cylinder" && otherData.type == "cylinder") {
          let vx = targetPosition.x - other.position.x;
          let vz = cargoData.diameter * 0.5 - other.position.z;

          let dv = Math.sqrt(vx*vx + vz*vz);

          vx /= dv;
          vz /= dv;

          let mult = otherData.diameter * 0.5 + cargoData.diameter * 0.5;

          const stickPosition = new THREE.Vector3(other.position.x + mult * vx, cargoData.height * 0.5, other.position.z + mult * vz);

          const dx = targetPosition.x - stickPosition.x;
          const dy = targetPosition.y - stickPosition.y;
          const dz = targetPosition.z - stickPosition.z;
          
          const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);

          if (distance < 200) {
            return stickPosition;
          }
        }
      }

      for (let other of this.cargos3d) {
        const otherData = other.userData;

        if (cargoData.cargoIndex == otherData.cargoIndex) continue;

        if (cargoData.type == "cylinder" && otherData.type == "cylinder") {
          let vx = targetPosition.x - other.position.x;
          let vz = (this.container.width - cargoData.diameter * 0.5) - other.position.z;

          let dv = Math.sqrt(vx*vx + vz*vz);

          vx /= dv;
          vz /= dv;

          let mult = otherData.diameter * 0.5 + cargoData.diameter * 0.5;

          const stickPosition = new THREE.Vector3(other.position.x + mult * vx, cargoData.height * 0.5, other.position.z + mult * vz);

          const dx = targetPosition.x - stickPosition.x;
          const dy = targetPosition.y - stickPosition.y;
          const dz = targetPosition.z - stickPosition.z;
          
          const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);

          if (distance < 200) {
            return stickPosition;
          }
        }
      }

      {
        const dy = targetPosition.y - cargoData.height * 0.5;
        const dz = targetPosition.z - cargoData.diameter * 0.5;

        const distance = Math.sqrt(dy*dy + dz*dz);

        if (distance < 200) {
          return new THREE.Vector3(targetPosition.x, cargoData.height * 0.5, cargoData.diameter * 0.5);
        }
      }

      {
        const dy = targetPosition.y - cargoData.height * 0.5;
        const dz = targetPosition.z - (this.container.width - cargoData.diameter * 0.5);

        const distance = Math.sqrt(dy*dy + dz*dz);

        if (distance < 200) {
          return new THREE.Vector3(targetPosition.x, cargoData.height * 0.5, this.container.width - cargoData.diameter * 0.5);
        }
      }

      for (let other of this.cargos3d) {
        const otherData = other.userData;

        if (cargoData.cargoIndex == otherData.cargoIndex) continue;

        if (cargoData.type == "cylinder" && otherData.type == "cylinder") {
          let vx = targetPosition.x - other.position.x;
          let vz = targetPosition.z - other.position.z;

          let dv = Math.sqrt(vx*vx + vz*vz);

          vx /= dv;
          vz /= dv;

          let mult = otherData.diameter * 0.5 + cargoData.diameter * 0.5;

          const stickPosition = new THREE.Vector3(other.position.x + mult * vx, cargoData.height * 0.5, other.position.z + mult * vz);

          const dx = targetPosition.x - stickPosition.x;
          const dy = targetPosition.y - stickPosition.y;
          const dz = targetPosition.z - stickPosition.z;
          
          const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);

          if (distance < 200) {
            return stickPosition;
          }
        }
      }

      return targetPosition;
    },

    // ===== ВРАЩЕНИЕ ГРУЗА =====
    rotateSelectedCargo() {
      if (!this.selectedCargo) return;
      const cargoData = this.selectedCargo.userData;
      if (cargoData.type === "box") {
        this.selectedCargo.rotation.y =
          (this.selectedCargo.rotation.y + Math.PI / 2) % (Math.PI * 2);
        let width = this.selectedCargo.userData.width;
        this.selectedCargo.userData.width = this.selectedCargo.userData.depth;
        this.selectedCargo.userData.depth = width;
        this.selectedCargo.userData.outline.rotation.copy(this.selectedCargo.rotation);
      } else if (cargoData.type === "cylinder") {
        this.selectedCargo.rotation.z =
          (this.selectedCargo.rotation.z + Math.PI / 2) % (Math.PI * 2);
      }
    },


calculateOptimalYPosition(cargo, targetX, targetZ) {
    // Сохраняем текущую позицию
    const originalPosition = cargo.position.clone();
    const originalRotation = cargo.rotation.clone();
    
    // Устанавливаем груз в целевую позицию на максимальной высоте
    cargo.position.set(targetX, this.container.height * 2, targetZ);
    
    const cargoData = cargo.userData;

    let height = this.ground + cargoData.height * 0.5;

    if (this.isInsideContainer2(targetX, targetZ, cargoData)) {
      height = cargoData.height * 0.5;
    }

    let cross = false;

    for (let other of this.cargos3d) {
      const otherData = other.userData;

      if (cargoData.cargoIndex == otherData.cargoIndex)
        continue;

      if (cargoData.type == "cylinder" && otherData.type == "cylinder") {
        var dx = cargo.position.x - other.position.x;
        var dz = cargo.position.z - other.position.z;

        var distance = Math.sqrt(dx*dx + dz*dz);

        var radiusSum = cargoData.diameter * 0.5 + otherData.diameter * 0.5;

        const eps = 1.0;

        if (distance + eps < radiusSum) {
          height = Math.max(height, other.position.y + otherData.height * 0.5 + cargoData.height * 0.5);
          cross = true;
        }
      }
      if (cargoData.type == "box" && otherData.type == "box") {
        var lx1 = cargo.position.x - cargoData.width * 0.5;
        var rx1 = cargo.position.x + cargoData.width * 0.5;

        var lx2 = other.position.x - otherData.width * 0.5;
        var rx2 = other.position.x + otherData.width * 0.5;

        var ly1 = cargo.position.z - cargoData.depth * 0.5;
        var ry1 = cargo.position.z + cargoData.depth * 0.5;

        var ly2 = other.position.z - otherData.depth * 0.5;
        var ry2 = other.position.z + otherData.depth * 0.5;

        var ixl = Math.max(lx1, lx2);
        var ixr = Math.min(rx1, rx2);

        var iyl = Math.max(ly1, ly2);
        var iyr = Math.min(ry1, ry2);

        if (ixl < ixr && iyl < iyr) {
          height = Math.max(height, other.position.y + otherData.height * 0.5 + cargoData.height * 0.5);
          cross = true;
        }
      }
    }

    if (!this.isInsideContainer2(targetX, targetZ, cargoData) && !cross) {
      height = this.ground + cargoData.height * 0.5;
    }
    
    cargo.position.y = height;
    
    const finalY = cargo.position.y;
    
    // Восстанавливаем оригинальную позицию
    cargo.position.copy(originalPosition);
    cargo.rotation.copy(originalRotation);
    
    return finalY;
},

  boundPosition(position) {
    const centerX = this.container.length / 2;
    const centerZ = this.container.width / 2;
    const size = this.maxSize / 2;

    let x = Math.max(centerX - size, position.x);
    x = Math.min(centerX + size, x);

    let z = Math.max(centerZ - size, position.z);
    z = Math.min(centerZ + size, z);

    return new THREE.Vector3(x, position.y, z)
  },

    // ===== ОБРАБОТКА МЫШИ =====
    onMouseMove(event) {
      const canvas = this.$refs.canvas
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      
    this.mouse.x = (x / rect.width) * 2 - 1;
    this.mouse.y = -(y / rect.height) * 2 + 1;

      if (this.isRotatingCamera && event.buttons === 1) {
        const deltaX = - (event.clientX - this.previousMousePosition.x);
        const deltaY = event.clientY - this.previousMousePosition.y;
        this.cameraRotation.y += deltaX * 0.01;
        this.cameraRotation.x += deltaY * 0.01;
        this.cameraRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.cameraRotation.x));
        this.updateCameraPosition();
      }

      this.previousMousePosition = { x: event.clientX, y: event.clientY };

    if (this.selectedCargo && event.buttons === 1) {
        this.raycaster.setFromCamera(this.mouse, this.camera);
        
        const cargoData = this.selectedCargo.userData;

        const planeNormal = new THREE.Vector3(0, 1, 0); // Горизонтальная плоскость
        const planePoint = cargoData.dragPosition.clone();
        planePoint.y = 0;
        this.dragPlane.setFromNormalAndCoplanarPoint(planeNormal, planePoint);
        
        this.raycaster.ray.intersectPlane(this.dragPlane, this.dragPoint);
        
        let newX = this.dragPoint.x;
        let newZ = this.dragPoint.z;
        
        let newY = this.calculateOptimalYPosition(this.selectedCargo, newX, newZ);
        
        let newPosition = new THREE.Vector3(newX, newY, newZ);

        cargoData.dragPosition = newPosition.clone();

        newPosition = this.trySnapToNeighbors(this.selectedCargo, newPosition);

        newY = this.calculateOptimalYPosition(this.selectedCargo, newPosition.x, newPosition.z);
        
        newPosition = new THREE.Vector3(newPosition.x, newY, newPosition.z);
        
        newPosition = this.boundPosition(newPosition);

        this.selectedCargo.position.copy(newPosition);
        
        cargoData.outline.position.copy(this.selectedCargo.position);
        cargoData.outline.rotation.copy(this.selectedCargo.rotation);
      }
    },

    onMouseDown(event) {
      if (event.buttons === 1) {
        this.isRotatingCamera = false;
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.cargos3d);
        if (intersects.length > 0) {
          this.previousMousePosition = { x: event.clientX, y: event.clientY };
          this.selectedCargo = intersects[0].object;
          this.selectedCargo.material.emissive.setHex(0x444444);
        } else {
          this.selectedCargo = null;
          this.isRotatingCamera = true;
          this.previousMousePosition = { x: event.clientX, y: event.clientY };
        }
      }
    },

    onMouseUp(event) {
      if (event.button === 2) {
        this.isRotatingCamera = false;
      }
      if (this.selectedCargo) {
        this.selectedCargo.material.emissive.setHex(0x000000);
      }
    },

    // ===== ОБРАБОТКА КОЛЕСИКА МЫШИ =====
    onWheel(event) {
    const canvasWrapper = document.querySelector('.canvasWrapper');
    
    // Проверяем, находится ли контейнер в конце страницы
    const rect = canvasWrapper.getBoundingClientRect();
    const isAtBottom = rect.bottom <= window.innerHeight + 10; // 50px допуск снизу

      if (!isAtBottom) {
        scrollDisabled.value = false;
        return;
      }
        scrollDisabled.value = true;

      event.preventDefault();

      // Изменяем расстояние камеры в зависимости от направления прокрутки
      const zoomSpeed = 0.001;
      const delta = event.deltaY * zoomSpeed;

      // Умножаем на текущее расстояние для пропорционального изменения зума
      const zoomFactor = 1 + delta;
      this.camera.userData.distance *= zoomFactor;

      // Ограничиваем минимальное и максимальное расстояние
      const maxDim = Math.max(this.container.length, this.container.width, this.container.height);
      this.camera.userData.distance = Math.max(
        maxDim * 0.5, // Минимальное расстояние
        Math.min(maxDim * 5, this.camera.userData.distance) // Максимальное расстояние
      );

      this.updateCameraPosition();
    },

    onKeyDown(event) {
      if (event.key === "r" || event.key === "R") {
        if (this.selectedCargo) {
          this.rotateSelectedCargo();
        }
      }
      if (event.key === "Escape") {
        if (this.selectedCargo) {
          this.selectedCargo.material.emissive.setHex(0x000000);
          this.selectedCargo = null;
          document.getElementById("selected-info").style.display = "none";
        }
      }
    },

    onWindowResize() {
      const wrapper = this.$refs.canvasWrapper
      this.camera.aspect = wrapper.clientWidth / wrapper.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(wrapper.clientWidth, wrapper.clientHeight);


      // пересчёт позиции камеры при ресайзе (адаптация к контейнеру)
      const maxDim = Math.max(this.container.length, this.container.width, this.container.height);
      const centerX = this.container.length / 2;
      const centerY = this.container.height / 2;
      const centerZ = this.container.width / 2;

      // используем сохранённые углы вращения и расстояние
      const distance = this.camera.userData.distance || maxDim * 2.8;
      this.camera.position.x = centerX + distance *
        Math.sin(this.cameraRotation.y) *
        Math.cos(this.cameraRotation.x);
      this.camera.position.y = centerY + distance * Math.sin(this.cameraRotation.x);
      this.camera.position.z = centerZ + distance *
        Math.cos(this.cameraRotation.y) *
        Math.cos(this.cameraRotation.x);

      this.camera.lookAt(centerX, centerY, centerZ);
    },

    // Запрет скролла
    disableScroll() {
      scrollDisabled.value = true;
    },

    enableScroll() {
      scrollDisabled.value = false;
    },

    init() {
      const wrapper = this.$refs.canvasWrapper
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xffffff);
      this.scene.fog = new THREE.Fog(0xffffff, 5000, 90000);

      // Камера: динамическое вычисление положения относительно контейнера
      const maxDim = Math.max(this.container.length, this.container.width, this.container.height);
      this.camera = new THREE.PerspectiveCamera(
        75,
        wrapper.clientWidth / wrapper.clientHeight,
        0.1,
        100000 // очень большая дальность для видимости всех объектов
      );

      // центр контейнера по всем осям
      const centerX = this.container.length / 2;
      const centerY = this.container.height / 2;
      const centerZ = this.container.width / 2;

      // динамическая позиция камеры с расчётом расстояния для обозрения всего контейнера
      const cameraDistance = maxDim * 0.45;
      this.camera.position.set(
        centerX + cameraDistance,
        centerY + cameraDistance,
        centerZ + cameraDistance
      );
      this.camera.lookAt(centerX, centerY, centerZ);

      // сохранение центра для вращения камеры
      this.cameraRotation = { x: 0, y: 0 };
      this.camera.userData.center = { x: centerX, y: centerY, z: centerZ };
      this.camera.userData.distance = cameraDistance;

      this.renderer = new THREE.WebGLRenderer({
        canvas: this.$refs.canvas,
        antialias: true,
        alpha: true,
      });
      this.renderer.setSize(wrapper.clientWidth, wrapper.clientHeight);
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFShadowMap;

      // Освещение
      const ambientLight = new THREE.AmbientLight(0xffffff, 1);
      this.scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(
        centerX + 120,
        centerY + 200,
        centerZ + 180
      );
      directionalLight.castShadow = true;
      directionalLight.shadow.camera.left = -500;
      directionalLight.shadow.camera.right = 500;
      directionalLight.shadow.camera.top = 500;
      directionalLight.shadow.camera.bottom = -500;
      directionalLight.shadow.camera.far = 1500;
      this.scene.add(directionalLight);

      // Raycaster
      this.raycaster = new THREE.Raycaster();

      this.createContainer();
      this.createCargos();
      this.createDoors();
      
      this.loadModels();

      wrapper.addEventListener("wheel", this.onWheel, { passive: false }); // Добавляем обработчик колесика
      wrapper.addEventListener("mousemove", this.onMouseMove, false);
      wrapper.addEventListener("mousedown", this.onMouseDown, false);
      wrapper.addEventListener("mouseup", this.onMouseUp, false);
      wrapper.addEventListener("mouseover", this.disableScroll, false);
      wrapper.addEventListener("mouseleave", this.enableScroll, false);
      window.addEventListener("contextmenu", (e) => e.preventDefault(), false);
      window.addEventListener("keydown", this.onKeyDown, false);
      window.addEventListener("resize", this.onWindowResize, false);

      // Изменяем расстояние камеры в зависимости от направления прокрутки
      const zoomSpeed = 0.001;
      const delta = 0 * zoomSpeed;

      // Умножаем на текущее расстояние для пропорционального изменения зума
      const zoomFactor = 1 + delta;
      this.camera.userData.distance *= zoomFactor;

      // Ограничиваем минимальное и максимальное расстояние
      this.camera.userData.distance = Math.max(
        maxDim * 0.5, // Минимальное расстояние
        Math.min(maxDim * 5, this.camera.userData.distance) // Максимальное расстояние
      );

      this.updateCameraPosition();

      this.animate();
    },
    
    async loadModels() {
      console.log("CONTAINER", this.container);

      const targetWidth = this.container.width; // желаемая ширина

      if (this.container.type == 2)
      {
        try {
          // Загружаем первую модель
          const model1 = await this.loadGLBModel(
            "/models/car-truck.glb"
          );

          const box1 = new THREE.Box3().setFromObject(model1);
          const size1 = box1.getSize(new THREE.Vector3());
          const center1 = box1.getCenter(new THREE.Vector3());
          
          model1.position.sub(center1);
          model1.rotation.y = - Math.PI / 2;

          const modelWidth1 = size1.z;
          const scale1 = targetWidth / modelWidth1 * 2;
          model1.scale.multiplyScalar(scale1);

          const centerX1 = 0;
          const centerY1 = this.ground - 50;
          const centerZ1 = this.container.width / 2;
          model1.position.set(centerX1, centerY1, centerZ1);

          this.scene.add(model1);

          // Загружаем вторую модель
          const model2 = await this.loadGLBModel(
            "/models/car-trailer.glb"
          );
          
          const box2 = new THREE.Box3().setFromObject(model2);
          const size2 = box2.getSize(new THREE.Vector3());
          const center2 = box2.getCenter(new THREE.Vector3());
          
          model2.position.sub(center2);
          model2.rotation.y = - Math.PI / 2;

          model2.scale.multiplyScalar(scale1);

          const centerX2 = this.container.length * 8.0 / 10.0;
          const centerY2 = this.ground - 50;
          const centerZ2 = this.container.width / 2;
          model2.position.set(centerX2, centerY2, centerZ2);

          this.scene.add(model2);
        } catch (error) {
          console.error("Ошибка при загрузке моделей:", error);
        }
      } else if (this.container.type == 1) {
          const model1 = await this.loadGLBModel(
            "/models/car-trailer.glb"
          );

          const box1 = new THREE.Box3().setFromObject(model1);
          const size1 = box1.getSize(new THREE.Vector3());
          const center1 = box1.getCenter(new THREE.Vector3());
          
          model1.position.sub(center1);
          model1.rotation.y = - Math.PI / 2;

          const modelWidth1 = size1.z;
          const scale1 = targetWidth / modelWidth1 * 1.5;
          model1.scale.multiplyScalar(scale1);

          const centerX1 = this.container.length * 2.0 / 10.0;
          const centerY1 = this.ground - 50;
          const centerZ1 = this.container.width / 2;
          model1.position.set(centerX1, centerY1, centerZ1);

          this.scene.add(model1);

          // Загружаем вторую модель
          const model2 = await this.loadGLBModel(
            "/models/car-trailer.glb"
          );
          
          const box2 = new THREE.Box3().setFromObject(model2);
          const size2 = box2.getSize(new THREE.Vector3());
          const center2 = box2.getCenter(new THREE.Vector3());
          
          model2.position.sub(center2);
          model2.rotation.y = - Math.PI / 2;

          model2.scale.multiplyScalar(scale1);

          const centerX2 = this.container.length * 8.0 / 10.0;
          const centerY2 = this.ground - 50;
          const centerZ2 = this.container.width / 2;
          model2.position.set(centerX2, centerY2, centerZ2);

          this.scene.add(model2);
      }

      console.log("done")
    },

    loadGLBModel(modelPath: string) {
      return new Promise((resolve, reject) => {
        const loader = new GLTFLoader();
        loader.load(
          modelPath,
          (gltf) => {
            const model = gltf.scene;
            
            // Опционально: установка теней
            model.traverse((child: any) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });

            resolve(model);
          },
          (progress) => {
            console.log(`Загрузка: ${(progress.loaded / progress.total * 100).toFixed(2)}%`);
          },
          (error) => {
            console.error("Ошибка загрузки GLB:", error);
            reject(error);
          }
        );
      });
    }
  },

  mounted() {
    this.buildCargos();
    this.init();
  },

  beforeUnmount() {
    cancelAnimationFrame(this.animationId);

    const wrapper = this.$refs.canvasWrapper
    wrapper.removeEventListener("wheel", this.onWheel, { passive: false });
    wrapper.removeEventListener("mousemove", this.onMouseMove, false);
    wrapper.removeEventListener("mousedown", this.onMouseDown, false);
    wrapper.removeEventListener("mouseup", this.onMouseUp, false);
    wrapper.removeEventListener("mouseover", this.disableScroll, false);
    wrapper.removeEventListener("mouseleave", this.enableScroll, false);
    //window.removeEventListener("contextmenu", (e) => e.preventDefault(), false);
    window.removeEventListener("keydown", this.onKeyDown, false);
    window.removeEventListener("resize", this.onWindowResize, false);
  },
});
